import React from "react";

// Shimmer primitive — a single grey block that pulses.
export const Sk = ({ className = "", style }) => (
  <div className={`shimmer rounded-[8px] ${className}`} style={style} />
);

// Replays a 150ms fade whenever the rendered value changes (key swap remounts).
export const SoftValue = ({ children, className = "" }) => (
  <span key={String(children)} className={`soft-val ${className}`}>{children}</span>
);

// Count-up roll: animates a dollar/number total from its previous value to the new one.
export function RollingNumber({ value, format = (n) => String(Math.round(n)), duration = 550, className = "" }) {
  const target = typeof value === "number" ? value : Number(String(value).replace(/[^0-9.-]/g, "")) || 0;
  const [disp, setDisp] = React.useState(target);
  const dispRef = React.useRef(target);
  const rafRef = React.useRef();
  React.useEffect(() => {
    const from = dispRef.current;
    if (from === target) { setDisp(target); return undefined; }
    const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { dispRef.current = target; setDisp(target); return undefined; }
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const e = 1 - Math.pow(1 - t, 3); // easeOutCubic
      const v = from + (target - from) * e;
      dispRef.current = v; setDisp(v);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else { dispRef.current = target; setDisp(target); }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return <span className={className}>{format(disp)}</span>;
}

// Mirrors the Check Ledger list rows (the metrics + pills chrome renders around it).
export function CheckLedgerSkeleton() {
  return (
    <div data-testid="ledger-skeleton" aria-busy="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-white rounded-[12px] mb-2.5 flex items-center gap-3 py-3 pl-4 pr-[18px]" style={{ border: "1px solid #E9E9EC" }}>
          <div className="flex-1 min-w-[220px]">
            <Sk className="h-[13px]" style={{ width: `${52 - i * 5}%` }} />
            <Sk className="h-[10px] mt-2 rounded-full" style={{ width: `${70 - i * 6}%` }} />
          </div>
          <div className="hidden md:block rounded-[10px] bg-black/[.03] px-3 py-2" style={{ flex: "1 1 300px", minWidth: 250 }}>
            <Sk className="h-[11px] w-[80%] rounded-full" /><Sk className="h-[11px] w-[60%] mt-2 rounded-full" />
          </div>
          <div className="hidden lg:block rounded-[10px] bg-black/[.03] px-3 py-2" style={{ flex: "1 1 224px", minWidth: 196 }}>
            <Sk className="h-[11px] w-[70%] rounded-full" /><Sk className="h-[11px] w-[50%] mt-2 rounded-full" />
          </div>
          <div className="text-right w-[104px] flex-shrink-0"><Sk className="h-[9px] w-[70px] ml-auto rounded-full" /><Sk className="h-[14px] w-[60px] ml-auto mt-2" /></div>
          <Sk className="h-[32px] w-[84px] rounded-[9px] flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}

// Mirrors NeedsYouBrief: title column + two money groups, segment row, three findings.
export function BriefSkeleton() {
  return (
    <div className="glass-surface rounded-[20px]" data-testid="home-skeleton-brief" aria-busy="true">
      <div className="flex flex-col lg:flex-row">
        <div className="lg:w-[236px] flex-shrink-0 p-5 lg:pr-6 lg:border-r border-black/[.07]">
          <Sk className="h-[15px] w-[120px]" />
          <Sk className="h-[11px] w-[160px] mt-3 rounded-full" />
        </div>
        {[0, 1].map((g) => (
          <div key={g} className={`flex-1 p-5 lg:px-6 ${g === 0 ? "lg:border-r border-black/[.07]" : ""}`}>
            <Sk className="h-[11px] w-[90px] rounded-full" />
            <div className="flex gap-6 mt-4">
              {[0, 1].map((p) => (
                <div key={p} className="flex-1">
                  <Sk className="h-[9px] w-[60px] rounded-full" />
                  <Sk className="h-[20px] w-[80px] mt-3" />
                  <Sk className="h-[10px] w-[100px] mt-3 rounded-full" />
                </div>
              ))}
            </div>
            <Sk className="h-[12px] w-[90px] mt-4 rounded-full" />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 px-5 py-3 border-t border-black/[.07]">
        {[70, 92, 70].map((w, i) => <Sk key={i} className="h-[30px] rounded-full" style={{ width: w }} />)}
      </div>
      <div className="px-5 pb-4">
        {[0, 1, 2].map((r) => (
          <div key={r} className="flex items-start gap-3 py-3" style={{ borderBottom: "1px solid #F1F1F4" }}>
            <Sk className="h-[14px] w-[14px] rounded-full flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[220px]">
              <Sk className="h-[13px]" style={{ width: `${64 - r * 8}%` }} />
              <Sk className="h-[11px] mt-2 rounded-full" style={{ width: `${82 - r * 6}%` }} />
              <Sk className="h-[10px] mt-2 w-[140px] rounded-full" />
            </div>
            <div className="w-[92px] flex-shrink-0"><Sk className="h-[15px] w-[64px] ml-auto" /></div>
            <div className="w-[152px] flex-shrink-0"><Sk className="h-[30px] w-[110px] ml-auto rounded-[9px]" /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Mirrors ClaimFormV20: black stats strip, claim head, two-column body (main + rail).
export function ClaimFormSkeleton() {
  const bar = { background: "rgba(255,255,255,.14)" };
  return (
    <div className="cfv" data-testid="claimform-skeleton" aria-busy="true">
      <div className="cfv-topbar">
        <div className="h-[12px] w-[130px] rounded" style={bar} />
        <div className="h-[12px] w-[90px] rounded" style={bar} />
        <div className="h-[12px] w-[200px] rounded" style={bar} />
      </div>
      <div className="cfv-claimhead">
        <div className="cfv-ch-icon" style={{ background: "#EDEDEF" }} />
        <div className="flex-1 min-w-0">
          <Sk className="h-[9px] w-[50px] rounded-full" />
          <Sk className="h-[18px] w-[220px] mt-2" />
          <Sk className="h-[11px] w-[320px] mt-2 rounded-full" />
        </div>
      </div>
      <div className="cfv-body">
        <div className="cfv-main">
          <div className="cfv-tiles">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="border rounded-[14px] p-3" style={{ borderColor: "#F2F2F4" }}>
                <Sk className="h-[9px] w-[70%] rounded-full" />
                <Sk className="h-[22px] w-[50%] mt-3" />
                <Sk className="h-[9px] w-[85%] mt-3 rounded-full" />
              </div>
            ))}
          </div>
          <Sk className="h-[13px] w-[180px] mt-2" />
          <div className="flex gap-2 flex-wrap mt-3">
            {[0, 1, 2, 3].map((i) => <Sk key={i} className="h-[30px] w-[110px] rounded-[10px]" />)}
          </div>
          <Sk className="h-[13px] w-[160px] mt-6" />
          <div className="mt-3 flex flex-col gap-2">
            {[0, 1, 2].map((i) => <Sk key={i} className="h-[44px] w-full rounded-[10px]" />)}
          </div>
        </div>
        <div className="cfv-rail">
          <Sk className="h-[10px] w-[80px] rounded-full" />
          <Sk className="h-[22px] w-[140px] mt-2" />
          <Sk className="h-[11px] w-full mt-3 rounded-full" />
          <Sk className="h-[11px] w-[80%] mt-2 rounded-full" />
          <Sk className="h-[80px] w-full mt-4 rounded-[12px]" />
          <Sk className="h-[80px] w-full mt-3 rounded-[12px]" />
        </div>
      </div>
    </div>
  );
}

// Mirrors CheckWorkspace body: two summary cards, claim segs, claim rows.
export function CheckWorkspaceSkeleton() {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto" data-testid="check-skeleton" aria-busy="true">
      <div className="max-w-[1080px] mx-auto px-6 py-6">
        <div className="grid md:grid-cols-2 gap-4 mb-5">
          <div className="rounded-[12px] border border-black/[.08] p-4">
            <Sk className="h-[9px] w-[70%] rounded-full" />
            <div className="flex gap-4 mt-3">{[0, 1, 2].map((i) => <Sk key={i} className="h-[14px] w-[70px]" />)}</div>
            <Sk className="h-[14px] w-[50%] mt-3" />
          </div>
          <div className="rounded-[12px] border border-black/[.08] p-3" style={{ background: "#FAFAFB" }}>
            <Sk className="h-[9px] w-[40px] rounded-full" />
            <Sk className="h-[34px] w-full mt-3 rounded-[9px]" />
            <Sk className="h-[34px] w-full mt-2 rounded-[9px]" />
          </div>
        </div>
        <div className="flex items-center gap-6 mb-3 pb-2" style={{ borderBottom: "1px solid #F1F1F4" }}>
          {[0, 1, 2, 3].map((i) => <Sk key={i} className="h-[14px] w-[70px]" />)}
        </div>
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3].map((i) => <Sk key={i} className="h-[56px] w-full rounded-[12px]" />)}
        </div>
      </div>
    </div>
  );
}

// Mirrors Create Claims list rows (chrome is already rendered around it).
export function ClaimRowsSkeleton() {
  return (
    <div data-testid="cc-skeleton" aria-busy="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-4" style={{ borderTop: "1px solid #F1F1F4" }}>
          <div className="flex-1 min-w-0">
            <Sk className="h-[13px]" style={{ width: `${40 - i * 4}%` }} />
            <Sk className="h-[10px] mt-2 rounded-full" style={{ width: `${60 - i * 5}%` }} />
          </div>
          <Sk className="h-[30px] w-[90px] rounded-[8px] flex-shrink-0" />
          <Sk className="h-[30px] w-[110px] rounded-[8px] flex-shrink-0" />
          <Sk className="h-[30px] w-[80px] rounded-[8px] flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}
